import { useDispatch, useStore } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import {
  mergeLocalizedEntryValues,
  selectTrainingLocalizationState,
  type LocalizedString,
} from "../../app/slices/TrainingLocalizationSlice";
import type { RootState } from "../../app/Store";
import { copyInteractionWithLocalizationFallback } from "../../helpers/InteractionCopyHelper";
import { IAnswer } from "../../models/Quiz";
import Take3D, { normalizeTake3D } from "../../models/Take3D";
import Interaction from "../../models/interactions/Interaction";
import {
  copySequenceInteraction,
  ISequence,
} from "../../models/interactions/SequenceModel";
import { getInteractionFromFactory } from "../interaction_factory/InteractionFactory";
import { useTrainingLocalization } from "../trainingLocalization/TrainingLocalizationHooks";
import type { ClipboardLocalizationSnapshot } from "./copyPasteClipboard";

export type LocalizationCopySource =
  | { mode: "clipboardV2"; snapshot?: ClipboardLocalizationSnapshot }
  | { mode: "currentTraining" };

const currentTrainingSource: LocalizationCopySource = {
  mode: "currentTraining",
};

export const useCopyInteraction = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { GetArrayOfKeys } = useTrainingLocalization();

  const copyLocalizedEntry = (
    originalKey: string,
    newKey: string,
    source: LocalizationCopySource,
  ) => {
    const localizationState = selectTrainingLocalizationState(store.getState());
    const sourceEntry =
      source.mode === "clipboardV2"
        ? source.snapshot?.entries[originalKey]
        : localizationState.localizedEntries[originalKey];

    if (!sourceEntry) return;

    const enabledLanguages = new Set(
      localizationState.localizedLanguages.map((language) => language.shortcode),
    );
    const entries: LocalizedString = Object.fromEntries(
      Object.entries(sourceEntry)
        .filter(([shortcode]) => enabledLanguages.has(shortcode))
        .map(([shortcode, localizedValue]) => [
          shortcode,
          { ...localizedValue },
        ]),
    );

    if (Object.keys(entries).length === 0) return;
    dispatch(mergeLocalizedEntryValues({ key: newKey, entries }));
  };

  const createCopyCallbacks = (source: LocalizationCopySource) => {
    const replaceKey = (originalKey: string, newKey: string): string => {
      copyLocalizedEntry(originalKey, newKey, source);
      return newKey;
    };

    const getAnswerArray = (
      originalAnswers: IAnswer[],
      newKeys: string[],
    ): IAnswer[] =>
      originalAnswers.map((answer, index) => {
        const newAnswer = {
          ...answer,
          id: uuidv4(),
          text: newKeys[index],
        };
        copyLocalizedEntry(answer.text, newAnswer.text, source);
        return newAnswer;
      });

    return { replaceKey, getAnswerArray };
  };

  const getInteractionCopy = (
    originalInteraction: Interaction,
    source: LocalizationCopySource = currentTrainingSource,
  ): Interaction | null => {
    const { replaceKey, getAnswerArray } = createCopyCallbacks(source);
    const factoryResult = getInteractionFromFactory(originalInteraction.type);
    if (!factoryResult?.Copy) {
      return copyInteractionWithLocalizationFallback(
        originalInteraction,
        GetArrayOfKeys,
        replaceKey,
        uuidv4,
      );
    }
    return factoryResult.Copy(
      originalInteraction,
      GetArrayOfKeys,
      replaceKey,
      getAnswerArray,
    );
  };

  const getSequenceCopy = (
    originalSequence: ISequence,
    source: LocalizationCopySource = currentTrainingSource,
  ): ISequence => {
    const { replaceKey, getAnswerArray } = createCopyCallbacks(source);
    return copySequenceInteraction(
      originalSequence,
      GetArrayOfKeys,
      replaceKey,
      getAnswerArray,
    );
  };

  const getTake3DCopyContent = (
    originalTake: Take3D,
    source: LocalizationCopySource = currentTrainingSource,
  ) => {
    const normalizedTake = normalizeTake3D(originalTake);

    return {
      interactions: normalizedTake.interactions
        .map((interaction) => getInteractionCopy(interaction, source))
        .filter(
          (interaction): interaction is Interaction => interaction !== null,
        ),
      sequence: getSequenceCopy(normalizedTake.sequence, source),
    };
  };

  return {
    getInteractionCopy,
    getSequenceCopy,
    getTake3DCopyContent,
  } as const;
};
